import { createContext, type ReactNode, useContext, useState } from "react";
import type { FormatType } from "@/class/Clock.ts";

interface FormatContextType {
    format: FormatType
    changeFormat: (s: FormatContextType["format"]) => void
}

const FormatContext = createContext<FormatContextType | null>(null)

/**
 * Hook personnalisé useFormat pour consommer le contexte.
 * Lance une erreur si utilisée en dehors du Provider afin d'éviter des accès indéfinis.
 */
export function useFormat() {
    const formatContext = useContext(FormatContext)
    if (!formatContext) throw new Error("Format context is not defined")
    return formatContext
}

/**
 * Provider qui encapsule l'application et fournit le format.
 * Le state conserve le format courant et expose une fonction pour le modifier.
 */
export function FormatContextProvider({ children }: { children: ReactNode }) {
    // Initialisation du format depuis la langue du navigateur avec un fallback.
    const [format, setFormat] = useState<FormatType>((navigator.language as FormatType) || "fr-FR")

    const changeFormat = (f: FormatType) => {
        setFormat(f)
    }

    return <FormatContext.Provider value={{ format, changeFormat }}>
        {children}
    </FormatContext.Provider>
}